import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Timer, Gift, Search, Map, FileText } from "lucide-react";
import { Reveal } from "@/components/od/Reveal";
import { MaskedLine } from "@/components/od/Hero";
import { useSimplify } from "@/components/od/simplify-context";

const SITE = "https://oceandata.ca";

const MINUTES = [
  {
    range: "Minutos 0 a 3",
    icon: Search,
    title: "Usted describe una semana ordinaria",
    body: "Sin presentación, sin jerga. Usted cuenta cómo circula el trabajo: los seguimientos, los reingresos de datos, los reportes armados a mano. Nosotros escuchamos.",
  },
  {
    range: "Minutos 3 a 10",
    icon: Map,
    title: "Detectamos dónde se esconden las horas",
    body: "Juntos trazamos el recorrido real de un proceso — del cliente al pago — y señalamos las fugas: la persecución, la reescritura, la búsqueda, la reunión de más.",
  },
  {
    range: "Minutos 10 a 15",
    icon: FileText,
    title: "Se va con la primera simplificación",
    body: "Los dos o tres lugares donde empezar, en orden, con lo que cada uno devolvería en horas. Útil trabaje con nosotros o no.",
  },
];

const FAQ = [
  {
    q: "¿Por qué es gratis?",
    a: "Porque 15 minutos de franqueza valen más que una hora de demostración. Si podemos ayudarle, lo sabrá rápido. Si no podemos, se lo diremos — y usted se queda con el mapa de sus fugas de todas formas.",
  },
  {
    q: "¿Es una llamada de ventas disfrazada?",
    a: "No. Sin secuencias de seguimiento, sin presión. Habla directamente con Bruno — 647 implementaciones desde 2017 — y la decisión de continuar es completamente suya.",
  },
  {
    q: "¿Qué pasa después, si hay encaje?",
    a: "Un solo proceso, a precio fijo, precedido de 30 días de prueba gratis sobre sus datos reales. Al final, un informe medido de las horas recuperadas — que usted conserva en cualquier caso.",
  },
  {
    q: "¿Para quién, exactamente?",
    a: "Empresas de construcción y servicios profesionales de 10 a 100 personas, en Miami, Montréal y todo Quebec, cuyas herramientas no se hablan: Excel, QuickBooks, Microsoft 365, Google, Zoho, Autodesk.",
  },
];

export default function EsRevisionPage() {
  const { setOpen } = useSimplify();

  useEffect(() => {
    document.title = "La revisión gratuita de 15 minutos | Ocean Data — Miami — Montréal";
    document.documentElement.lang = "es";
    const el = document.createElement("link");
    el.rel = "alternate";
    el.hreflang = "es";
    el.href = `${SITE}/es/revision-gratuita-15-minutos`;
    el.setAttribute("data-hreflang-injected", "true");
    document.head.appendChild(el);
    return () => {
      document.documentElement.lang = "en";
      el.remove();
    };
  }, []);

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${SITE}/es/revision-gratuita-15-minutos`,
        name: "La revisión gratuita de 15 minutos",
        description:
          "Quince minutos, sus cifras, una respuesta honesta. Ocean Data detecta los dos o tres lugares donde su empresa de construcción o servicios profesionales podría simplificar primero. Miami, Montréal y todo Quebec.",
        inLanguage: "es",
        isPartOf: { "@id": `${SITE}/#website` },
        about: { "@id": `${SITE}/#org` },
      },
      {
        "@type": "Service",
        name: "Revisión de negocio gratuita de 15 minutos",
        provider: { "@id": `${SITE}/#org` },
        areaServed: [
          { "@type": "City", name: "Miami" },
          { "@type": "City", name: "Montréal" },
        ],
        description:
          "Una revisión gratuita y sin presión de adónde van las horas de su equipo — el punto de partida de cada proyecto Ocean Data.",
        offers: { "@type": "Offer", price: "0", priceCurrency: "CAD" },
      },
      {
        "@type": "FAQPage",
        inLanguage: "es",
        mainEntity: FAQ.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <main className="pt-16 sm:pt-20 bg-[#F8FAFC]" lang="es">
      {/* Hero */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-6">
            Miami — Montréal · Sin compromiso
          </p>
          <h1
            data-testid="esrev-headline"
            className="font-display font-bold tracking-tight leading-[1.06] text-brand-navy text-4xl sm:text-5xl lg:text-6xl max-w-3xl"
          >
            <MaskedLine delay={0.15} testId="esrev-headline-line-1">La revisión gratuita</MaskedLine>
            <MaskedLine delay={0.3} testId="esrev-headline-line-2">
              <span className="text-brand-teal">de 15 minutos.</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed"
          >
            Quince minutos, sus cifras, una respuesta honesta. Usted se va con
            los dos o tres lugares donde su empresa podría simplificar primero
            — trabaje con nosotros o no.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-9 flex flex-col sm:flex-row sm:items-center gap-5"
          >
            <button
              data-testid="esrev-cta-hero"
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-3 rounded-full bg-brand-navy text-white text-base font-semibold px-9 py-4 transition-all duration-300 hover:bg-brand-teal hover:-translate-y-0.5"
            >
              <Timer size={18} />
              Reservar mis 15 minutos
            </button>
            <p className="text-sm text-slate-500">Respuesta personal en un día hábil</p>
          </motion.div>
        </div>
      </section>

      {/* Minuto a minuto */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-5">
            Minuto a minuto
          </p>
          <h2 className="font-display font-bold tracking-tight text-brand-navy text-3xl sm:text-4xl max-w-2xl mb-14">
            Esto es exactamente lo que pasa.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-7">
            {MINUTES.map((m, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div data-testid={`esrev-minute-${i}`} className="rounded-2xl bg-[#F8FAFC] border border-slate-200 p-7 sm:p-8 h-full transition-all duration-300 hover:border-brand-teal hover:-translate-y-0.5">
                  <span className="w-10 h-10 rounded-full bg-brand-tealsoft text-brand-teal flex items-center justify-center mb-4">
                    <m.icon size={17} />
                  </span>
                  <p className="font-mono text-xs uppercase tracking-[0.18em] text-brand-teal font-semibold mb-2">{m.range}</p>
                  <h3 className="font-display font-semibold text-xl text-brand-navy mb-3">{m.title}</h3>
                  <p className="text-sm sm:text-base text-slate-600 leading-relaxed">{m.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Lo que usted conserva */}
      <section className="py-16 lg:py-24 bg-brand-navy text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-gold font-semibold mb-5">
                Lo que usted conserva
              </p>
              <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl leading-tight mb-6">
                El mapa de sus fugas, en cualquier caso.
              </h2>
              <p className="text-sm sm:text-base text-slate-300 leading-relaxed mb-6">
                Aunque nunca trabajemos juntos, usted se va con una imagen
                clara: adónde van las horas de su equipo, en qué orden
                recuperarlas, y cuánto vale. Es nuestra forma de empezar cada
                relación — con valor, no con una cotización.
              </p>
              <div className="flex items-center gap-3 text-brand-gold">
                <Gift size={18} />
                <p className="font-display font-semibold text-lg">Gratis. Sin seguimiento. Sin trampa.</p>
              </div>
            </div>
            <div className="rounded-2xl bg-brand-navysurf border border-white/10 p-7 sm:p-9">
              <p className="font-mono text-xs uppercase tracking-[0.18em] text-slate-400 font-semibold mb-6">Después de sus 15 minutos</p>
              <ul className="space-y-4">
                {[
                  "Sus 2 o 3 primeras simplificaciones, en orden",
                  "Las horas que cada una podría devolver",
                  "Una respuesta honesta: podemos ayudar, o no",
                  "Si hay encaje: 30 días de prueba gratis sobre sus datos reales",
                ].map((s, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm sm:text-base text-slate-300 leading-relaxed">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-brand-gold/15 text-brand-gold flex items-center justify-center font-mono text-[10px] font-bold shrink-0">
                      {i + 1}
                    </span>
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-5">
            Preguntas frecuentes
          </p>
          <h2 className="font-display font-bold tracking-tight text-brand-navy text-3xl sm:text-4xl mb-12">
            Lo que preguntan antes de reservar
          </h2>
          <div className="space-y-4">
            {FAQ.map((f, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <details data-testid={`esrev-faq-${i}`} className="group rounded-2xl bg-white border border-slate-200 px-7 py-6 open:border-brand-teal transition-colors">
                  <summary className="cursor-pointer list-none flex items-center justify-between gap-4 font-display font-semibold text-lg text-brand-navy">
                    {f.q}
                    <span className="text-brand-teal text-xl transition-transform duration-300 group-open:rotate-45">+</span>
                  </summary>
                  <p className="mt-4 text-sm sm:text-base text-slate-600 leading-relaxed">{f.a}</p>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <Reveal>
            <div className="rounded-3xl bg-brand-navy text-white p-10 sm:p-14 flex flex-col lg:flex-row lg:items-center gap-8 justify-between">
              <div className="max-w-xl">
                <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl leading-tight mb-4">
                  Quince minutos ahora. Horas cada semana después.
                </h2>
                <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
                  Los equipos con los que trabajamos recuperan entre 25 y 40 %
                  de su tiempo administrativo — medido, no prometido. Todo
                  empieza con un cuarto de hora.
                </p>
              </div>
              <div className="flex flex-col items-start gap-4 shrink-0">
                <button
                  data-testid="esrev-cta-final"
                  onClick={() => setOpen(true)}
                  className="inline-flex items-center gap-3 rounded-full bg-brand-teal text-white text-base font-semibold px-9 py-4 transition-all duration-300 hover:bg-brand-tealhover hover:-translate-y-0.5"
                >
                  Reservar mis 15 minutos
                  <ArrowRight size={18} />
                </button>
                <p className="text-xs text-slate-400">
                  ¿Curioso por el método?{" "}
                  <Link to="/es/mantenga-sus-herramientas" className="text-brand-gold hover:text-white transition-colors">
                    Mantenga sus herramientas. Las conectamos.
                  </Link>
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
    </main>
  );
}
